import React, { useState, useEffect, useRef, useCallback } from "react"
import { library } from "@fortawesome/fontawesome-svg-core"
import { fas }     from "@fortawesome/free-solid-svg-icons"
library.add(fas)

import { getRequest, postRequest, deleteRequest, pathname } from "@react/helpers/fetch"

import Card        from "@react/shared/ui/Card"
import Header      from "./components/Header"
import StatsStrip  from "./components/StatsStrip"
import QuotesTable from "./components/QuotesTable"
import Pagination  from "./components/Pagination"
import DuplicateQuoteModal from "./components/DuplicateQuoteModal"
import NewQuoteModal       from "./components/NewQuoteModal"
import ImportFromFileModal from "./components/ImportFromFileModal"
import ConfigureImportModal from "./components/ConfigureImportModal"
import CsvExportModal      from "./components/CsvExportModal"
import CreateProjectModal  from "./components/CreateProjectModal"
import ValidationModal     from "@react/shared/validation/ValidationModal"

const DEBOUNCE_MS = 300

// A DPGF import takes minutes, so the page re-fetches while one is running.
const IMPORT_POLL_MS = 5000

const FETCH_PARAMS = ["page", "order", "direction", "type"]

// Filters, a saved view or a search arrive only in the page URL; re-fetches must keep them.
const urlFilterQuery = () => {
  const query = new URLSearchParams(window.location.search)
  FETCH_PARAMS.forEach(key => query.delete(key))
  return query.toString()
}

const App = ({ data: initialData }) => {
  const [data,      setData]      = useState(initialData)
  const [page,      setPage]      = useState(initialData.pagination?.current_page || 1)
  const [order,     setOrder]     = useState(initialData.order)
  const [direction, setDirection] = useState(initialData.direction)
  const [loading,   setLoading]   = useState(false)
  const [duplicatingQuote, setDuplicatingQuote] = useState(null)
  const [creatingOpen,     setCreatingOpen]     = useState(false)
  const [importingOpen,    setImportingOpen]    = useState(false)
  const [configuringQuote, setConfiguringQuote] = useState(null)
  const [exportingOpen,    setExportingOpen]    = useState(false)
  const [convertingQuote,  setConvertingQuote]  = useState(null)
  const [validatingQuote,  setValidatingQuote]  = useState(null)

  const updateValidation = (quoteId, validation) =>
    setData(current => ({
      ...current,
      quotes: current.quotes.map(quote => quote.id === quoteId ? { ...quote, validation: validation.badge } : quote),
    }))

  const [filterQuery] = useState(urlFilterQuery)

  const indexUrl     = initialData.urls.index
  const didMountRef  = useRef(false)
  const debounceRef  = useRef(null)
  const requestIdRef = useRef(0)

  const fetchIndex = useCallback(() => {
    const requestId = ++requestIdRef.current
    const params    = { page, order, direction, type: "projects" }
    const withFilters = path => (filterQuery ? `${path}&${filterQuery}` : path)

    setLoading(true)
    return getRequest(withFilters(pathname({ path: indexUrl, format: "json", params }))).then(json => {
      if (requestId !== requestIdRef.current) return
      setData(json)
      setLoading(false)
      window.history.replaceState(null, "", withFilters(pathname({ path: indexUrl, params })))
    })
  }, [indexUrl, filterQuery, page, order, direction])

  useEffect(() => {
    if (!didMountRef.current) { didMountRef.current = true; return }
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(fetchIndex, DEBOUNCE_MS)
    return () => clearTimeout(debounceRef.current)
  }, [fetchIndex])

  const importing = (data.quotes || []).some(quote => quote.import_running || quote.destroying)

  useEffect(() => {
    if (!importing) return
    const timer = setInterval(fetchIndex, IMPORT_POLL_MS)
    return () => clearInterval(timer)
  }, [importing, fetchIndex])

  const handleSortChange = (nextOrder, nextDirection) => {
    setOrder(nextOrder); setDirection(nextDirection); setPage(1)
  }

  const openExport = () => setExportingOpen(true)

  const runDuplicate = (url, successMessage) =>
    postRequest({ url })
      .then(response => {
        if (response && response.errors) {
          flashAlert(Array.isArray(response.errors) ? response.errors.join(", ") : String(response.errors))
          return
        }
        flashNotice(successMessage)
        fetchIndex()
      })
      .catch(() => flashAlert(I18n.t("quotes.builder.footer.network_error")))

  const handleDuplicate = quote => setDuplicatingQuote(quote)

  const submitDuplicate = values => {
    const params = {
      "quote[title]":     values.title,
      "quote[client_id]": values.client_id,
      "quote[site_id]":   values.site_id,
    }
    const url = pathname({ path: duplicatingQuote.duplicate_url, params })
    return runDuplicate(url, I18n.t("quotes.duplicate.success"))
      .then(() => setDuplicatingQuote(null))
  }

  const handleNewVersion = quote =>
    postRequest({ url: quote.new_version_url })
      .then(response => {
        if (response && response.errors) {
          flashAlert(Array.isArray(response.errors) ? response.errors.join(", ") : String(response.errors))
          return
        }
        if (response?.id) window.location.href = `/quotes/${response.id}/edit`
      })
      .catch(() => flashAlert(I18n.t("quotes.builder.footer.network_error")))

  const handleCreateProject = quote => setConvertingQuote(quote)

  const handleDelete = quote => {
    if (!window.confirm(I18n.t("button.sure"))) return
    deleteRequest(quote.destroy_url)
      .then(response => {
        if (response && response.errors) {
          window.alert(Array.isArray(response.errors) ? response.errors.join(", ") : String(response.errors))
          return
        }
        fetchIndex()
      })
      .catch(() => window.alert(I18n.t("quotes.builder.footer.network_error")))
  }

  const handleSaveAsTemplate = quote => {
    postRequest({ url: quote.save_as_template_url })
      .then(response => {
        if (!response?.success) {
          flashAlert(response?.errors?.join(", ") || I18n.t("quotes.builder.footer.network_error"))
          return
        }
        flashNotice(response.notice || I18n.t("quotes.project_templates.save_as_template.success"))
      })
      .catch(() => flashAlert(I18n.t("quotes.builder.footer.network_error")))
  }

  return (
    <div className="font-sans bg-slate-50 min-h-screen text-slate-900">
      <div className="px-8 py-8">
        <Header
          count          = {data.stats?.count ?? data.pagination?.total_count ?? 0}
          onExport       = {openExport}
          onNewClick     = {() => setCreatingOpen(true)}
          onImportClick  = {() => setImportingOpen(true)}
          templatesUrl   = {data.urls?.templates_index}
        />
        <StatsStrip stats={data.stats} />
        <Card className={`rounded-lg relative ${loading ? "opacity-75" : ""}`}>
          <QuotesTable
            quotes          = {data.quotes}
            order           = {order}
            direction       = {direction}
            onSortChange    = {handleSortChange}
            onValidation    = {setValidatingQuote}
            onConfigureImport = {setConfiguringQuote}
            onCreateProject  = {handleCreateProject}
            onDuplicate      = {handleDuplicate}
            onNewVersion     = {handleNewVersion}
            onSaveAsTemplate = {handleSaveAsTemplate}
            onDelete         = {handleDelete}
          />
          <Pagination
            pagination   = {data.pagination}
            onPageChange = {setPage}
          />
        </Card>
      </div>

      <DuplicateQuoteModal
        isOpen   = {duplicatingQuote !== null}
        quote    = {duplicatingQuote}
        onClose  = {() => setDuplicatingQuote(null)}
        onSubmit = {submitDuplicate}
      />

      <NewQuoteModal
        isOpen      = {creatingOpen}
        onClose     = {() => setCreatingOpen(false)}
        newQuoteUrl   = {data.urls.new_quote}
        managers      = {data.managers}
        currentUserId = {data.current_user_id}
      />

      <ImportFromFileModal
        isOpen    = {importingOpen}
        onClose   = {() => setImportingOpen(false)}
        importUrl = {data.urls.import_from_file}
        managers      = {data.managers}
        currentUserId = {data.current_user_id}
      />

      <ConfigureImportModal
        quote        = {configuringQuote}
        onClose      = {() => setConfiguringQuote(null)}
        onConfigured = {() => { setConfiguringQuote(null); fetchIndex() }}
      />

      <CsvExportModal
        isOpen       = {exportingOpen}
        onClose      = {() => setExportingOpen(false)}
        csvUrl       = {data.urls.csv}
        ids          = {data.ids}
        count        = {data.pagination?.total_count ?? 0}
        defaultEmail = {data.current_user_email}
      />

      <ValidationModal
        isOpen   = {validatingQuote !== null}
        onClose  = {() => setValidatingQuote(null)}
        url      = {validatingQuote?.validation_url}
        onChange = {validation => validatingQuote && updateValidation(validatingQuote.id, validation)}
      />

      <CreateProjectModal
        isOpen          = {convertingQuote !== null}
        onClose         = {() => setConvertingQuote(null)}
        eyebrow         = {convertingQuote?.number}
        convertUrl      = {convertingQuote?.convert_to_project_url}
        managers        = {data.managers}
        currentUserId   = {data.current_user_id}
        defaultStartDate = {convertingQuote?.creation_date}
      />
    </div>
  )
}

export default App
